import {
  Box,
  Button,
  Callout,
  HighlightIcon,
  Skeleton,
} from '@dev-spendesk/grapes';
import { useSelector } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import type { Payable } from 'modules/bookkeep/payables/containers/PayablePanelContainer';
import { useGetInvoiceCardDetails } from 'modules/cards/hooks/usetGetInvoiceCardFromPayable';
import { routeFor, routes } from 'src/core/constants/routes';
import { getIsInvoiceToCardEnabled } from 'src/core/selectors/globalSelectors';

type Props = {
  payable: Payable;
};

export const InvoiceCardSection = ({ payable }: Props) => {
  const { t } = useTranslation('global');

  const companyId = useCompanyId();
  const isInvoiceToCardFeatureEnabled = useSelector(getIsInvoiceToCardEnabled);

  const getInvoiceCardDetails = useGetInvoiceCardDetails({
    payableId: payable.id,
    companyId,
    isEnabled:
      isInvoiceToCardFeatureEnabled && payable.type === 'invoicePurchase',
  });

  if (!isInvoiceToCardFeatureEnabled) {
    return null;
  }

  if (getInvoiceCardDetails.isLoading) {
    return (
      <div className="relative top-[-6px] mb-[-8px] h-0 w-full overflow-visible">
        <Skeleton width="100%" height="8px" />
      </div>
    );
  }

  if (getInvoiceCardDetails.isError) {
    return (
      <Callout
        variant="warning"
        title={t('cards.errorLoadingInvoiceDetails')}
        className="flex items-center"
      >
        <Button
          variant="secondaryWarning"
          text={t('lazyLoading.error.retry')}
          onClick={() => getInvoiceCardDetails.refetch}
        />
      </Callout>
    );
  }

  const result = getInvoiceCardDetails.data;

  if (
    !result?.requestId ||
    !result?.invoiceStatus ||
    !['to_pay', 'partially_paid'].includes(result.invoiceStatus)
  ) {
    return <></>;
  }

  return (
    <>
      <Box
        variant="secondary"
        className="relative flex min-h-[80px] flex-col justify-center gap-16 py-16"
      >
        <div className="flex items-center">
          <HighlightIcon
            name="clock-filled"
            size={32}
            variant="blue"
            className="mr-12"
          />
          <div className="title-m">
            {result.invoiceStatus === 'to_pay'
              ? t('cards.awaitingCardPayment')
              : t('cards.processingCardPayment')}
          </div>
          {result.hasCardAccess && (
            <div className="ml-auto">
              <ReactLink
                to={`${routeFor(routes.SINGLE_USE_CARDS.path, {
                  company: companyId,
                  id: result.requestId,
                })}`}
              >
                <Button
                  iconName="chevron-right"
                  iconPosition="right"
                  text={t('misc.goToCard')}
                  variant="secondaryNeutral"
                />
              </ReactLink>
            </div>
          )}
        </div>
      </Box>
    </>
  );
};
